import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getCurrencySymbol } from '../utils/currency';

interface Branch {
  id: number;
  branch_name: string;
}

interface Supplier {
  id: number;
  name: string;
  phone_number: string;
  aadhar?: string;
  address?: string;
}

interface InventoryItem {
  id: number;
  inventory_id: string;
  imei_mac_address: string;
  category: 'mobile_phone' | 'laptop' | 'tablet' | 'accessories';
  phone_model: string;
  branch_id: number;
  buying_price: number;
  selling_price: number;
  service_expense: number;
  package_expense: number;
  status: 'ready' | 'reserved' | 'service' | 'sold';
  date_added: string;
  supplier?: Supplier;
  acquisition_notes?: string;
  payment_method?: 'cash' | 'bank_transfer' | 'upi';
}

interface AddInventoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => Promise<void>;
  branches: Branch[];
  suppliers: Supplier[];
  editItem?: InventoryItem | null;
  loading: boolean;
  showToast: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
}

const AddInventoryModal: React.FC<AddInventoryModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  branches,
  suppliers,
  editItem,
  loading,
  showToast
}) => {
  // Form data state - local to this component
  const [formData, setFormData] = useState({
    imei_mac_address: '',
    phone_model: '',
    buying_price: '',
    selling_price: '',
    acquisition_notes: '',
    supplier_name: '',
    supplier_phone: '',
    supplier_aadhar: '',
    supplier_address: '',
    date_added: new Date().toISOString().split('T')[0]
  });

  // Modal state
  const [selectedModalCategory, setSelectedModalCategory] = useState('mobile_phone');
  const [selectedModalBranch, setSelectedModalBranch] = useState('');
  const [selectedModalStatus, setSelectedModalStatus] = useState('ready');
  const [selectedModalPaymentMethod, setSelectedModalPaymentMethod] = useState('cash');
  const [selectedSupplier, setSelectedSupplier] = useState<number | null>(null);
  const [isNewSupplier, setIsNewSupplier] = useState(false);
  const [imeiError, setImeiError] = useState('');

  // Dropdown states
  const [isModalCategoryDropdownOpen, setIsModalCategoryDropdownOpen] = useState(false);
  const [isModalBranchDropdownOpen, setIsModalBranchDropdownOpen] = useState(false);
  const [isModalStatusDropdownOpen, setIsModalStatusDropdownOpen] = useState(false);
  const [isModalPaymentMethodDropdownOpen, setIsModalPaymentMethodDropdownOpen] = useState(false);

  // Supplier search states
  const [supplierSearchTerm, setSupplierSearchTerm] = useState('');
  const [supplierSearchResults, setSupplierSearchResults] = useState<Supplier[]>([]);
  const [hasSearchedSupplier, setHasSearchedSupplier] = useState(false);

  // Options
  const modalCategoryOptions = [
    { value: 'mobile_phone', label: 'Mobile Phone' },
    { value: 'laptop', label: 'Laptop' },
    { value: 'tablet', label: 'Tablet' },
    { value: 'accessories', label: 'Accessories' }
  ];

  const modalStatusOptions = [
    { value: 'ready', label: 'Ready' },
    { value: 'service', label: 'Service' },
    { value: 'reserved', label: 'Reserved' }
  ];

  const modalPaymentMethodOptions = [
    { value: 'cash', label: 'Cash' },
    { value: 'upi', label: 'UPI' },
    { value: 'bank_transfer', label: 'Bank Transfer' }
  ];

  // Initialize form when editing
  useEffect(() => {
    if (editItem) {
      setFormData({
        imei_mac_address: editItem.imei_mac_address,
        phone_model: editItem.phone_model,
        buying_price: editItem.buying_price.toString(),
        selling_price: (editItem.selling_price || 0).toString(),
        acquisition_notes: editItem.acquisition_notes || '',
        supplier_name: editItem.supplier?.name || '',
        supplier_phone: editItem.supplier?.phone_number || '',
        supplier_aadhar: editItem.supplier?.aadhar || '',
        supplier_address: editItem.supplier?.address || '',
        date_added: editItem.date_added || new Date().toISOString().split('T')[0]
      });
      setSelectedModalCategory(editItem.category);
      setSelectedModalBranch(editItem.branch_id.toString());
      setSelectedModalStatus(editItem.status);
      setSelectedModalPaymentMethod(editItem.payment_method || 'cash');
      if (editItem.supplier?.id) {
        setSelectedSupplier(editItem.supplier.id);
      }
    } else {
      resetForm();
    }
  }, [editItem, isOpen]);

  const resetForm = () => {
    setFormData({
      imei_mac_address: '',
      phone_model: '',
      buying_price: '',
      selling_price: '',
      acquisition_notes: '',
      supplier_name: '',
      supplier_phone: '',
      supplier_aadhar: '',
      supplier_address: '',
      date_added: new Date().toISOString().split('T')[0]
    });
    setSelectedModalCategory('mobile_phone');
    setSelectedModalBranch('');
    setSelectedModalStatus('ready');
    setSelectedModalPaymentMethod('cash');
    setSelectedSupplier(null);
    setIsNewSupplier(false);
    setSupplierSearchTerm('');
    setSupplierSearchResults([]);
    setHasSearchedSupplier(false);
    setImeiError('');
  };

  const closeAllModalDropdowns = () => {
    setIsModalCategoryDropdownOpen(false);
    setIsModalBranchDropdownOpen(false);
    setIsModalStatusDropdownOpen(false);
    setIsModalPaymentMethodDropdownOpen(false);
  };

  const handleModalClose = () => {
    resetForm();
    onClose();
  };

  const validateImeiOnBlur = () => {
    const requiresValidation = selectedModalCategory === 'mobile_phone' || selectedModalCategory === 'tablet';
    if (requiresValidation && formData.imei_mac_address) {
      const digits = formData.imei_mac_address.replace(/\D/g, '').length;
      if (digits !== 15) {
        setImeiError(`IMEI must be 15 digits (currently ${digits})`);
      } else {
        setImeiError('');
      }
    } else {
      setImeiError('');
    }
  };

  const handleSupplierSearch = () => {
    const searchTerm = supplierSearchTerm.trim().toLowerCase();
    if (!searchTerm) {
      setSupplierSearchResults([]);
      setHasSearchedSupplier(false);
      return;
    }

    const results = suppliers.filter(supplier => {
      const nameMatch = supplier.name?.toLowerCase().includes(searchTerm);
      const phoneMatch = supplier.phone_number?.toLowerCase().includes(searchTerm);
      const aadharMatch = supplier.aadhar?.toLowerCase().includes(searchTerm);
      return nameMatch || phoneMatch || aadharMatch;
    });

    setSupplierSearchResults(results);
    setHasSearchedSupplier(true);
  };

  const handleClearSupplier = () => {
    setSelectedSupplier(null);
    setSupplierSearchTerm('');
    setSupplierSearchResults([]);
    setHasSearchedSupplier(false);
  };

  const handleSupplierPhoneChange = (phone: string) => {
    setFormData(prev => ({ ...prev, supplier_phone: phone }));

    // Auto-fill if phone matches existing supplier
    if (phone.length === 10) {
      const existingSupplier = suppliers.find(s => s.phone_number === phone);
      if (existingSupplier) {
        setFormData(prev => ({
          ...prev,
          supplier_name: existingSupplier.name,
          supplier_aadhar: existingSupplier.aadhar || '',
          supplier_address: existingSupplier.address || ''
        }));
        showToast(`Supplier "${existingSupplier.name}" found and auto-filled!`, 'info');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.phone_model.trim()) {
      alert('Please enter the device model');
      return;
    }

    if (!formData.imei_mac_address.trim()) {
      alert('Please enter the IMEI/MAC address');
      return;
    }

    if (!selectedModalBranch) {
      alert('Please select a branch');
      return;
    }

    if (!formData.buying_price || parseFloat(formData.buying_price) <= 0) {
      alert('Please enter a valid buying price');
      return;
    }

    // Validate IMEI for mobile phones and tablets
    if ((selectedModalCategory === 'mobile_phone' || selectedModalCategory === 'tablet')) {
      const imeiDigits = formData.imei_mac_address.replace(/\D/g, '');
      if (imeiDigits.length !== 15) {
        alert('IMEI number must be exactly 15 digits for mobile phones and tablets.');
        return;
      }
    }

    const payload: any = {
      date_added: formData.date_added,
      category: selectedModalCategory,
      phone_model: formData.phone_model,
      imei_mac_address: formData.imei_mac_address,
      buying_price: parseFloat(formData.buying_price),
      service_expense: editItem ? editItem.service_expense : 0,
      package_expense: editItem ? editItem.package_expense : 0,
      selling_price: parseFloat(formData.selling_price) || 0,
      branch_id: parseInt(selectedModalBranch),
      status: selectedModalStatus,
      payment_method: selectedModalPaymentMethod,
      acquisition_notes: formData.acquisition_notes || null
    };

    // Add supplier info
    if (selectedSupplier) {
      payload.supplier_id = selectedSupplier;
    } else if (isNewSupplier && formData.supplier_name && formData.supplier_phone) {
      payload.new_supplier = {
        name: formData.supplier_name,
        phone_number: formData.supplier_phone,
        aadhar: formData.supplier_aadhar || null,
        address: formData.supplier_address || null
      };
    }

    await onSubmit(payload);
  };

  const getSelectedCategoryLabel = () => {
    const category = modalCategoryOptions.find(cat => cat.value === selectedModalCategory);
    return category ? category.label : selectedModalCategory;
  };

  const getSelectedStatusLabel = () => {
    const status = modalStatusOptions.find(stat => stat.value === selectedModalStatus);
    return status ? status.label : selectedModalStatus;
  };

  const getSelectedPaymentLabel = () => {
    const payment = modalPaymentMethodOptions.find(pay => pay.value === selectedModalPaymentMethod);
    return payment ? payment.label : selectedModalPaymentMethod;
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
      onClick={handleModalClose}
    >
      <div
        className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto relative m-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">{editItem ? 'Edit Inventory Item' : 'Add New Inventory Item'}</h2>
          <button
            onClick={handleModalClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form className="space-y-6" onClick={closeAllModalDropdowns} onSubmit={handleSubmit}>
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Model *
              </label>
              <input
                type="text"
                value={formData.phone_model}
                onChange={(e) => setFormData({...formData, phone_model: e.target.value})}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                placeholder="Enter device model"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category *
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    closeAllModalDropdowns();
                    setIsModalCategoryDropdownOpen(!isModalCategoryDropdownOpen);
                  }}
                  className="w-full flex items-center justify-between px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                >
                  <span className="text-sm text-gray-700">{getSelectedCategoryLabel()}</span>
                  <svg className={`w-4 h-4 text-gray-400 transition-transform ${isModalCategoryDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {isModalCategoryDropdownOpen && (
                  <div className="absolute left-0 mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[9999]">
                    {modalCategoryOptions.map((option, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedModalCategory(option.value);
                          setIsModalCategoryDropdownOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                          selectedModalCategory === option.value ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {(selectedModalCategory === 'mobile_phone' || selectedModalCategory === 'tablet') ? 'IMEI Number *' : 'IMEI/MAC Address *'}
              </label>
              <input
                type="text"
                value={formData.imei_mac_address}
                onChange={(e) => setFormData({...formData, imei_mac_address: e.target.value})}
                onBlur={validateImeiOnBlur}
                onFocus={() => setImeiError('')}
                className={`w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 bg-white transition-colors h-10 ${
                  imeiError ? 'border-red-500 focus:ring-red-500' : 'border-gray-200 focus:ring-primary-500 hover:border-gray-300'
                }`}
                placeholder={(selectedModalCategory === 'mobile_phone' || selectedModalCategory === 'tablet') ? 'Enter 15-digit IMEI number' : 'Enter IMEI or MAC address'}
                required
              />
              {imeiError && (
                <p className="text-xs mt-1 text-red-600">{imeiError}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Branch *
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    closeAllModalDropdowns();
                    setIsModalBranchDropdownOpen(!isModalBranchDropdownOpen);
                  }}
                  className="w-full flex items-center justify-between px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                >
                  <span className="text-sm text-gray-700">
                    {selectedModalBranch ? branches.find(b => b.id.toString() === selectedModalBranch)?.branch_name : 'Select Branch'}
                  </span>
                  <svg className={`w-4 h-4 text-gray-400 transition-transform ${isModalBranchDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {isModalBranchDropdownOpen && (
                  <div className="absolute left-0 mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[9999]">
                    {branches.map((branch, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedModalBranch(branch.id.toString());
                          setIsModalBranchDropdownOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                          selectedModalBranch === branch.id.toString() ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700'
                        }`}
                      >
                        {branch.branch_name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Buying Price *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-gray-500">{getCurrencySymbol()}</span>
                <input
                  type="text"
                  value={formData.buying_price}
                  onChange={(e) => setFormData({...formData, buying_price: e.target.value})}
                  className="w-full pl-8 pr-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                  placeholder="0"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Selling Price (Min Price)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-gray-500">{getCurrencySymbol()}</span>
                <input
                  type="text"
                  value={formData.selling_price}
                  onChange={(e) => setFormData({...formData, selling_price: e.target.value})}
                  className="w-full pl-8 pr-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                  placeholder="0"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    closeAllModalDropdowns();
                    setIsModalStatusDropdownOpen(!isModalStatusDropdownOpen);
                  }}
                  className="w-full flex items-center justify-between px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                >
                  <span className="text-sm text-gray-700">{getSelectedStatusLabel()}</span>
                  <svg className={`w-4 h-4 text-gray-400 transition-transform ${isModalStatusDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {isModalStatusDropdownOpen && (
                  <div className="absolute left-0 mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[9999]">
                    {modalStatusOptions.map((option, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedModalStatus(option.value);
                          setIsModalStatusDropdownOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                          selectedModalStatus === option.value ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Method
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    closeAllModalDropdowns();
                    setIsModalPaymentMethodDropdownOpen(!isModalPaymentMethodDropdownOpen);
                  }}
                  className="w-full flex items-center justify-between px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                >
                  <span className="text-sm text-gray-700">{getSelectedPaymentLabel()}</span>
                  <svg className={`w-4 h-4 text-gray-400 transition-transform ${isModalPaymentMethodDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {isModalPaymentMethodDropdownOpen && (
                  <div className="absolute left-0 mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[9999]">
                    {modalPaymentMethodOptions.map((option, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedModalPaymentMethod(option.value);
                          setIsModalPaymentMethodDropdownOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                          selectedModalPaymentMethod === option.value ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date Added
              </label>
              <input
                type="date"
                value={formData.date_added}
                onChange={(e) => setFormData({...formData, date_added: e.target.value})}
                max={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
              />
            </div>
          </div>

          {/* Supplier Information */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Supplier Information (Optional)</h3>

            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={isNewSupplier}
                  onChange={(e) => setIsNewSupplier(e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Add new supplier</span>
              </label>
            </div>

            {!isNewSupplier && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search Existing Supplier
                </label>

                {/* Selected Supplier Display */}
                {selectedSupplier && (() => {
                  const supplier = suppliers.find(s => s.id === selectedSupplier);
                  return (
                    <div className="mb-3 p-3 bg-primary-50 border border-primary-200 rounded-lg flex items-start justify-between">
                      <div>
                        <p className="text-sm font-medium text-primary-800">{supplier?.name}</p>
                        <div className="mt-1 space-y-0.5">
                          <p className="text-xs text-primary-600">
                            <span className="text-primary-400">Phone:</span> {supplier?.phone_number || 'Not available'}
                          </p>
                          <p className="text-xs text-primary-600">
                            <span className="text-primary-400">Aadhar:</span> {supplier?.aadhar || 'Not available'}
                          </p>
                          <p className="text-xs text-primary-600">
                            <span className="text-primary-400">Address:</span> {supplier?.address || 'Not available'}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleClearSupplier}
                        className="p-1.5 text-primary-600 hover:text-primary-800 hover:bg-primary-100 rounded-lg transition-colors"
                        title="Clear selection"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  );
                })()}

                {/* Search Input */}
                {!selectedSupplier && (
                  <>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        </div>
                        <input
                          type="text"
                          value={supplierSearchTerm}
                          onChange={(e) => setSupplierSearchTerm(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleSupplierSearch())}
                          className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                          placeholder="Search by name, phone, or aadhar..."
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleSupplierSearch}
                        className="px-4 py-2.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors h-10 flex items-center gap-2 whitespace-nowrap"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        Search
                      </button>
                    </div>

                    {/* Search Results */}
                    {supplierSearchResults.length > 0 && (
                      <div className="mt-3 border border-gray-200 rounded-lg max-h-60 overflow-y-auto">
                        <div className="space-y-2 p-2">
                          {supplierSearchResults.map((supplier) => (
                            <div
                              key={supplier.id}
                              className="flex items-start justify-between p-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900">{supplier.name}</p>
                                <div className="mt-1 space-y-0.5">
                                  <p className="text-xs text-gray-500">
                                    <span className="text-gray-400">Phone:</span> {supplier.phone_number || 'Not available'}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    <span className="text-gray-400">Aadhar:</span> {supplier.aadhar || 'Not available'}
                                  </p>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedSupplier(supplier.id);
                                  setSupplierSearchTerm('');
                                  setSupplierSearchResults([]);
                                  setHasSearchedSupplier(false);
                                }}
                                className="ml-3 px-3 py-1.5 bg-primary-500 text-white text-sm rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
                              >
                                Select
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* No Results */}
                    {supplierSearchResults.length === 0 && hasSearchedSupplier && supplierSearchTerm && (
                      <div className="mt-3 p-3 text-sm text-gray-500 text-center border border-gray-200 rounded-lg">
                        No suppliers found. Try a different search term or add a new supplier.
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {isNewSupplier && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Supplier Name *
                  </label>
                  <input
                    type="text"
                    value={formData.supplier_name}
                    onChange={(e) => setFormData({...formData, supplier_name: e.target.value})}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                    placeholder="Enter supplier name"
                    required={isNewSupplier}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Supplier Phone *
                  </label>
                  <input
                    type="tel"
                    value={formData.supplier_phone}
                    onChange={(e) => handleSupplierPhoneChange(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                    placeholder="Enter 10-digit phone number"
                    required={isNewSupplier}
                  />
                  <p className="text-xs text-gray-400 mt-1">Auto-fills if supplier exists</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Supplier Aadhar
                  </label>
                  <input
                    type="text"
                    value={formData.supplier_aadhar}
                    onChange={(e) => setFormData({...formData, supplier_aadhar: e.target.value})}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                    placeholder="XXXX-XXXX-XXXX"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Supplier Address
                  </label>
                  <input
                    type="text"
                    value={formData.supplier_address}
                    onChange={(e) => setFormData({...formData, supplier_address: e.target.value})}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                    placeholder="Enter supplier address"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Acquisition Notes
            </label>
            <textarea
              rows={3}
              value={formData.acquisition_notes}
              onChange={(e) => setFormData({...formData, acquisition_notes: e.target.value})}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors resize-none"
              placeholder="Enter any notes about the item condition, issues, etc."
            ></textarea>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-6 border-t">
            <button
              type="button"
              onClick={handleModalClose}
              className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
              disabled={loading}
            >
              {loading ? (editItem ? 'Updating...' : 'Adding...') : (editItem ? 'Update Inventory Item' : 'Add Inventory Item')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default React.memo(AddInventoryModal);
